import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, APIResponse } from '../client';
import type { User, Branch } from '../queries/settings.queries';

interface CreateUserInput {
  email: string;
  password: string;
  name: string;
  role: string;
  branchId?: string;
  isActive: boolean;
}

interface UpdateUserInput {
  email?: string;
  name?: string;
  role?: string;
  branchId?: string;
  isActive?: boolean;
  password?: string;
}

interface CreateBranchInput {
  name: { en: string; th: string };
  code: string;
  address: string;
  phone: string;
  type: 'branch' | 'warehouse';
  isActive: boolean;
  openingHours?: {
    day: string;
    open: string;
    close: string;
  }[];
}

interface UpdateBranchInput {
  name?: { en: string; th: string };
  code?: string;
  address?: string;
  phone?: string;
  type?: 'branch' | 'warehouse';
  isActive?: boolean;
  openingHours?: {
    day: string;
    open: string;
    close: string;
  }[];
}

// Create user
export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateUserInput) => {
      const response = await apiClient.post<APIResponse<{ user: User }>>('/users', data);
      return response.data.data.user;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

// Update user
export function useUpdateUser(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateUserInput) => {
      const response = await apiClient.patch<APIResponse<{ user: User }>>(
        `/users/${userId}`,
        data
      );
      return response.data.data.user;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user', userId] });
    },
  });
}

// Delete user
export function useDeleteUser(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await apiClient.delete(`/users/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

// Create branch
export function useCreateBranch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateBranchInput) => {
      const response = await apiClient.post<APIResponse<{ branch: Branch }>>('/branches', data);
      return response.data.data.branch;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branches'] });
    },
  });
}

// Update branch
export function useUpdateBranch(branchId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateBranchInput) => {
      const response = await apiClient.patch<APIResponse<{ branch: Branch }>>(
        `/branches/${branchId}`,
        data
      );
      return response.data.data.branch;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branches'] });
      queryClient.invalidateQueries({ queryKey: ['branch', branchId] });
    },
  });
}

// Delete branch
export function useDeleteBranch(branchId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await apiClient.delete(`/branches/${branchId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branches'] });
    },
  });
}

export type { CreateUserInput, UpdateUserInput, CreateBranchInput, UpdateBranchInput };
